// Planning mutations (phase 4), spread into `mutations` in shared/mutations.ts. All plain
// create/update/remove, so the dashboard shows each change instantly. Rules:
// server/src/mutations/planning.ts. The math they feed is shared/planning.ts.
import { z } from "zod";
import { create, custom, remove, update } from "./mutation-kit.ts";

const text = (max = 200) => z.string().trim().max(max);
const isoDate = z.iso.date("Use a date like 2026-12-31");
const percent = z.number().min(50, "Use a target from 50 to 99.9%").max(99.9, "Use a target from 50 to 99.9%");
const wholeDays = (min: number, max: number) => z.number().int("Use whole days").min(min, `Use ${min} to ${max} days`).max(max, `Use ${min} to ${max} days`);

export const POLICIES = ["auto", "manual", "none"] as const;
// Make planning --- auto: the nightly run drafts its POs (never sends them; server/src/auto-reorder.ts).
export const POLICY_LABELS = { auto: "Auto-reorder (drafts POs nightly)", manual: "Suggest, I order", none: "Don't plan" } as const;

export const planningSettingsFields = z.object({
  review_days: wholeDays(1, 90),
  default_lead_time_days: wholeDays(0, 365),
  cover_days: wholeDays(1, 730),
  horizon_days: wholeDays(31, 730),
  overstock_months: z.number().positive("Must be more than 0").max(120),
  service_a: percent,
  service_b: percent,
  service_c: percent,
  /** Cutover (M2) --- orders placed before HHL Ops are on POs here now: the plans stop saying they aren't. */
  prior_orders_entered: z.boolean(),
  /** Stock counts --- how big a day's list is, and the most on hand a SKU can have to be on one (migration 070). */
  count_units_per_day: z.number().int("Whole units").min(1, "At least 1").max(100_000),
  count_skus_per_day: z.number().int("Whole SKUs").min(1, "At least 1").max(500),
  count_skip_over: z.number().int("Whole units").min(1, "At least 1").max(10_000_000),
});

export const STOCK_POLICIES = ["forecast", "min_max", "target"] as const;
export const STOCK_POLICY_LABELS = { forecast: "Forecast + safety stock", min_max: "Min / max", target: "Target level" } as const;
const units = z.number().nonnegative("Must be 0 or more").max(10_000_000);

export const itemPlanFields = z.object({
  policy: z.enum(POLICIES),
  safety_stock: z.number().nonnegative("Must be 0 or more").nullable(),
  service_level: percent.nullable(),
  snooze_until: isoDate.nullable(),
  snooze_reason: text(500),
  /** Hidden from the plan --- why it's set to not planned ("Discontinued"). */
  hidden_reason: text(200),
  notes: text(5000),
  // Forecasting --- (rules: server/src/mutations/planning.ts)
  stock_policy: z.enum(STOCK_POLICIES),
  min_qty: units.nullable(),
  max_qty: units.nullable(),
  target_qty: units.nullable(),
  /** "Forecast like" another sell item, at like_pct %. */
  like_item_id: z.uuid().nullable(),
  like_pct: z.number().positive("Use a percentage above 0").max(1000, "Use up to 1,000%"),
});

/** Guessed inputs --- vendor link terms the plan's bulk bar sets (the same rules as vendor_item's fields). */
export const bulkTermsFields = z.object({
  lead_time_days: z.number().int("Use whole days").min(0, "Use 0 to 730 days").max(730, "Use 0 to 730 days").nullable(),
  moq: z.number().nonnegative("MOQ must be 0 or more").max(10_000_000),
  order_multiple: z.number().positive("The multiple must be more than 0").max(10_000_000),
  unit_price: z.number().nonnegative("Must be 0 or more").nullable(),
});

export const ADJUSTMENT_SCOPES = ["all", "vendor", "project", "product_type", "tag", "item"] as const;
export const ADJUSTMENT_SCOPE_LABELS = {
  all: "All items",
  vendor: "Vendor",
  project: "Project",
  product_type: "Product type",
  tag: "Tag",
  item: "Item",
} as const;

export const forecastAdjustmentFields = z.object({
  scope: z.enum(ADJUSTMENT_SCOPES),
  /** Vendor, project or item id, product type or tag ('' for all). */
  scope_value: text(200),
  pct: z.number().gt(-100, "Use more than −100%").max(1000, "Use up to +1,000%"),
  /** Any day in the month; saved as the month's first day. null = open-ended. */
  month_from: isoDate.nullable(),
  month_to: isoDate.nullable(),
  note: text(500),
  active: z.boolean(),
});

export const forecastOverrideFields = z.object({
  item_id: z.uuid(),
  /** Any day in the month; saved as the month's first day. */
  month: isoDate,
  units: z.number().nonnegative("Units must be 0 or more").max(10_000_000),
  note: text(500),
});

export const b2bDemandFields = z.object({
  item_id: z.uuid(),
  qty: z.number().positive("Quantity must be more than 0").max(10_000_000),
  due_date: isoDate,
  note: text(500),
  done: z.boolean(),
});

// Vendor closures --- days a vendor doesn't work (rules: server/src/mutations/planning.ts). A country's closure covers
// every vendor there (vendor.country); a vendor's own row is its own closure, or with `replaces_id` its own dates for
// a country's (the same day twice: it doesn't close).
const country = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^([A-Z]{2})?$/, "Use a 2-letter country code, e.g. CN");
export const vendorClosureFields = z.object({
  label: text(100).min(1, "Name it, e.g. Chinese New Year"),
  closed_from: isoDate,
  /** The first day back at work (not closed). */
  back_on: isoDate,
  country,
  vendor_id: z.uuid().nullable(),
  replaces_id: z.uuid().nullable(),
  notes: text(2000),
});

export const planningMutations = {
  // One row (made by migration 009); create only puts it back if it's gone.
  "planning_settings.create": create("planning_settings", planningSettingsFields.partial()),
  "planning_settings.update": update("planning_settings", planningSettingsFields),
  /** Per-item settings; `id` is the item's id. */
  "item_plan.create": create("item_plan", itemPlanFields.partial()),
  "item_plan.update": update("item_plan", itemPlanFields),
  "forecast_override.create": create("forecast_override", forecastOverrideFields.partial().required({ item_id: true, month: true, units: true })),
  "forecast_override.update": update("forecast_override", forecastOverrideFields.omit({ item_id: true })),
  "forecast_override.remove": remove("forecast_override"),
  "b2b_demand.create": create("b2b_demand", b2bDemandFields.partial().required({ item_id: true, qty: true, due_date: true })),
  "b2b_demand.update": update("b2b_demand", b2bDemandFields.omit({ item_id: true })),
  "b2b_demand.remove": remove("b2b_demand"),
  // Forecasting ---
  "forecast_adjustment.create": create("forecast_adjustment", forecastAdjustmentFields.partial().required({ scope: true, pct: true })),
  "forecast_adjustment.update": update("forecast_adjustment", forecastAdjustmentFields),
  "forecast_adjustment.remove": remove("forecast_adjustment"),
  // Vendor closures --- who it covers is set when it's made; dates, name and notes change after.
  "vendor_closure.create": create("vendor_closure", vendorClosureFields.partial().required({ label: true, closed_from: true, back_on: true })),
  "vendor_closure.update": update("vendor_closure", vendorClosureFields.pick({ label: true, closed_from: true, back_on: true, notes: true })),
  "vendor_closure.remove": remove("vendor_closure"),
  /**
   * Guessed inputs --- the Purchasing plan's bulk bar: each row's vendor link (`link_id`, the one the plan uses) gets the
   * terms given and its item the policy or snooze given; fields left out stay as they are. One transaction.
   */
  "plan.bulk_edit": custom(
    z.object({
      items: z
        .array(z.object({ item_id: z.uuid(), link_id: z.uuid().nullable() }))
        .min(1, "Pick at least one item")
        .max(500, "Change up to 500 at a time"),
      terms: bulkTermsFields.partial().default({}),
      plan: itemPlanFields.pick({ policy: true, snooze_until: true, snooze_reason: true }).partial().default({}),
    }),
  ),
} as const;
